import type { Page } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { SearchX, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import type { BaseListQuery, ListQueryControls } from '../../../shared/hooks/use-list-query';

interface DirectoryTableProps<Q extends BaseListQuery, V extends { id: string }> {
  page: Page<V>;
  columns: TableColumnsType<V>;
  list: ListQueryControls<Q>;
  isRefreshing: boolean;
  /** Repeated inside the empty state when the directory has nothing yet. */
  newButton: ReactNode;
  emptyIcon: LucideIcon;
  emptyTitle: string;
  emptyDescription: string;
  searchPlaceholder: string;
  noMatchTitle: string;
  /** Extra controls beside the search box, such as a date range. */
  toolbar?: ReactNode;
}

/** Search box plus the server-paged table, shared by garages and suppliers; the old rows stay up, dimmed, while the next page loads. */
export function DirectoryTable<Q extends BaseListQuery, V extends { id: string }>({
  page,
  columns,
  list,
  isRefreshing,
  newButton,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  searchPlaceholder,
  noMatchTitle,
  toolbar,
}: DirectoryTableProps<Q, V>) {
  if (page.total === 0 && !list.isFiltered) {
    return (
      <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription}>
        {newButton}
      </EmptyState>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <ListSearch value={list.query.q} placeholder={searchPlaceholder} onSearch={(q) => list.setQuery({ q } as Partial<Q>, { replace: true })} />
        {toolbar}
      </div>
      <Table
        rowKey="id"
        dataSource={page.items}
        columns={columns}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={
          list.isFiltered
            ? { emptyText: <EmptyState icon={SearchX} title={noMatchTitle} description="Nothing matches this search and these filters." /> }
            : undefined
        }
        {...list.tableProps<V>(page.total)}
      />
    </>
  );
}
